import React from 'react';
import { Box, Button, Stack, Typography } from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';

const ALL_LEAGUES = ['NFL', 'NBA', 'NHL', 'MMA', 'NASCAR', 'CFB', 'MLB', 'PGA'];

interface AllAccessUpsellBannerProps {
  subscribedLeagues: string[];
  onUpgrade?: () => void;
}

const AllAccessUpsellBanner: React.FC<AllAccessUpsellBannerProps> = ({
  subscribedLeagues = [],
  onUpgrade,
}) => {
  const handleUpgradeClick = () => {
    if (onUpgrade) {
      onUpgrade();
    } else {
      window.location.href = 'https://stokastic.com/pricing?sport=all%20sports';
    }
  };

  // Format the subscribed leagues for display
  const formatLeagueList = (leagues: string[]): string => {
    if (leagues.length === 0) return '';
    if (leagues.length === 1) return leagues[0];
    if (leagues.length === 2) return `${leagues[0]} and ${leagues[1]}`;
    return `${leagues.slice(0, -1).join(', ')}, and ${leagues[leagues.length - 1]}`;
  };

  // Get leagues user doesn't have access to
  const lockedLeagues = ALL_LEAGUES.filter(l => !subscribedLeagues.includes(l));
  const lockedLeaguesDisplay = lockedLeagues.slice(0, 3); // Show first 3 locked leagues

  const formatLockedLeagues = (): string => {
    if (lockedLeaguesDisplay.length === 0) return 'every league';
    const display = formatLeagueList(lockedLeaguesDisplay);
    if (lockedLeagues.length > 3) {
      return `${display}, and every league`;
    }
    return display;
  };

  // Don't render if no subscribed leagues
  if (subscribedLeagues.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        bgcolor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        p: { xs: 2, md: 3 },
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
      }}
    >
      <Stack spacing={0.5}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <CheckIcon
            sx={{
              fontSize: 18,
              color: '#22C55E',
            }}
          />
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 500,
              fontSize: 14,
              color: 'white',
            }}
          >
            You currently have access to {formatLeagueList(subscribedLeagues)} Contest Simulations
          </Typography>
        </Stack>
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 400,
            fontSize: 14,
            color: 'rgba(255, 255, 255, 0.6)',
            pl: 3.5,
          }}
        >
          Upgrade to All Access to unlock {formatLockedLeagues()}
        </Typography>
      </Stack>

      <Button
        variant="outlined"
        onClick={handleUpgradeClick}
        sx={{
          borderColor: '#FF6B4A',
          color: '#FF6B4A',
          fontFamily: 'Inter, sans-serif',
          fontWeight: 600,
          fontSize: 14,
          textTransform: 'none',
          borderRadius: '8px',
          px: 3,
          py: 1,
          whiteSpace: 'nowrap',
          flexShrink: 0,
          '&:hover': {
            borderColor: '#FF8A6A',
            bgcolor: 'rgba(255, 107, 74, 0.1)',
          },
        }}
      >
        Unlock All Access
      </Button>
    </Box>
  );
};

export default AllAccessUpsellBanner;
